/**
 * The archive / restore / destroy conversation, in one place (PRD §5.2).
 *
 * A hard delete additionally demands the typed confirmation the PRD asks for; archiving is a
 * plain yes/no because it destroys nothing. The typed word lives in the body component, which
 * exists only while the dialog is open, so reopening never inherits what was typed last time.
 */

import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  /** When set, the button stays disabled until the word is typed exactly. */
  typedConfirmation?: string;
  typedHint?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  /** Optional test id on the dialog panel. */
  testId?: string;
  confirmTestId?: string;
  cancelTestId?: string;
}

export function ConfirmDialog(props: ConfirmDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent data-testid={props.testId}>
        {props.open && <ConfirmBody {...props} />}
      </DialogContent>
    </Dialog>
  );
}

function ConfirmBody({
  onOpenChange,
  title,
  body,
  confirmLabel,
  typedConfirmation,
  typedHint,
  destructive = false,
  busy = false,
  onConfirm,
  confirmTestId,
  cancelTestId,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const locked = typedConfirmation !== undefined && typed.trim() !== typedConfirmation;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{body}</DialogDescription>
      </DialogHeader>

      {typedConfirmation !== undefined && (
        <div className="space-y-2">
          <Input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={typedConfirmation}
            aria-label={typedHint}
          />
          <p className="text-xs text-muted-foreground">{typedHint}</p>
        </div>
      )}

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={busy}
          data-testid={cancelTestId}
        >
          {t("common.cancel")}
        </Button>
        <Button
          variant={destructive ? "destructive" : "default"}
          onClick={onConfirm}
          disabled={busy || locked}
          data-testid={confirmTestId}
        >
          {confirmLabel}
        </Button>
      </DialogFooter>
    </>
  );
}
